import { format } from "date-fns";
import { useNavigate } from "react-router-dom";
import { buildTelehealthSessionPath, buildProviderSessionNotesPath } from "@/routes";
import {
  Calendar,
  Clock,
  Video,
  FileText,
  CheckCircle,
  XCircle,
  AlertCircle,
} from "lucide-react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

interface Appointment {
  id: string;
  scheduled_date: string;
  scheduled_time: string;
  visit_type: string;
  visit_reason: string | null;
  status: string;
  actual_duration_minutes: number | null;
  cpt_code: string | null;
  notes: string | null;
}

interface PatientAppointmentsProps {
  appointments: Appointment[];
  patientId: string;
}

export function PatientAppointments({ appointments, patientId }: PatientAppointmentsProps) {
  const navigate = useNavigate();

  const getStatusIcon = (status: string) => {
    switch (status) {
      case "completed":
        return <CheckCircle className="h-4 w-4 text-green-600" />;
      case "cancelled":
      case "no_show":
        return <XCircle className="h-4 w-4 text-destructive" />;
      case "in_progress":
        return <Video className="h-4 w-4 text-blue-600" />;
      default:
        return <Clock className="h-4 w-4 text-muted-foreground" />;
    }
  };

  const getStatusVariant = (status: string) => {
    switch (status) {
      case "completed":
        return "default";
      case "cancelled":
      case "no_show":
        return "destructive";
      case "in_progress":
        return "default";
      case "waiting":
        return "secondary";
      default:
        return "outline";
    }
  };

  const upcomingAppointments = appointments.filter(
    (apt) => new Date(apt.scheduled_date) >= new Date() && apt.status === "scheduled"
  );

  const pastAppointments = appointments.filter(
    (apt) => new Date(apt.scheduled_date) < new Date() || apt.status !== "scheduled"
  );

  return (
    <div className="space-y-6">
      {/* Upcoming Appointments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Calendar className="h-5 w-5" />
            Upcoming Appointments
          </CardTitle>
          <CardDescription>Scheduled sessions for this patient</CardDescription>
        </CardHeader>
        <CardContent>
          {upcomingAppointments.length > 0 ? (
            <div className="space-y-3">
              {upcomingAppointments.map((apt) => (
                <div
                  key={apt.id}
                  className="flex items-center justify-between p-4 rounded-lg border bg-muted/30"
                >
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 rounded-lg bg-primary/10 flex items-center justify-center">
                      <Video className="h-6 w-6 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">
                        {format(new Date(apt.scheduled_date), "EEEE, MMMM d, yyyy")}
                      </p>
                      <p className="text-sm text-muted-foreground">
                        {apt.scheduled_time} • {apt.visit_type}
                      </p>
                      {apt.visit_reason && (
                        <p className="text-sm text-muted-foreground mt-1">
                          Reason: {apt.visit_reason}
                        </p>
                      )}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Badge variant="secondary">Scheduled</Badge>
                    <Button
                      size="sm"
                      onClick={() => navigate(buildTelehealthSessionPath(apt.id))}
                    >
                      <Video className="h-4 w-4 mr-2" />
                      Start Session
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Calendar className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No upcoming appointments</p>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Past Appointments */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            Session History
          </CardTitle>
          <CardDescription>Past appointments and their outcomes</CardDescription>
        </CardHeader>
        <CardContent>
          {pastAppointments.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Type</TableHead>
                  <TableHead>Duration</TableHead>
                  <TableHead>CPT Code</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {pastAppointments.map((apt) => (
                  <TableRow key={apt.id}>
                    <TableCell className="font-medium">
                      {format(new Date(apt.scheduled_date), "MMM d, yyyy")}
                    </TableCell>
                    <TableCell>{apt.scheduled_time}</TableCell>
                    <TableCell className="capitalize">{apt.visit_type}</TableCell>
                    <TableCell>
                      {apt.actual_duration_minutes
                        ? `${apt.actual_duration_minutes} min`
                        : "—"}
                    </TableCell>
                    <TableCell>
                      {apt.cpt_code ? (
                        <Badge variant="outline">{apt.cpt_code}</Badge>
                      ) : (
                        "—"
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        {getStatusIcon(apt.status)}
                        <Badge variant={getStatusVariant(apt.status)}>
                          {apt.status.replace("_", " ")}
                        </Badge>
                      </div>
                    </TableCell>
                    <TableCell>
                      {apt.status === "completed" && (
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => navigate(buildProviderSessionNotesPath(apt.id))}
                        >
                          <FileText className="h-4 w-4 mr-2" />
                          Notes
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="text-center py-8 text-muted-foreground">
              <Clock className="h-8 w-8 mx-auto mb-2 opacity-50" />
              <p>No session history</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
